/* ============================================================================
   Slide 2 — "Mandukya Upanishad" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide2Inner (desktop) and HTLDSlide2Mobile. Edit copy ONLY
   here — English and German side by side; the German object must have exactly
   the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent2 = "#7f77dd";

/** A rich-text segment: plain, bold, or emphasised. `hi` = brightest highlight (desktop). */
export interface Seg { t: string; b?: boolean; em?: boolean; hi?: boolean; }

export interface AumState { sanskrit: string; english: string; symbol: string; description: string; }

/** Sanskrit name of the state card that is highlighted (the dream state). */
export const slide2HighlightSanskrit = "Svapna";

const en = {
  meta: {
    breadcrumbPre: "Ancient Roots",
    breadcrumbAccent: "Mandukya Upanishad",
    headerPlain: "Ancient",
    headerAccent: "Wisdom",
    titlePlain: "Mandukya",
    titleAccent: "Upanishad",
    subtitle: "The ancient text that mapped consciousness 3,000 years ago",
    inspirationLabel: "The inspiration",
    aumLabel: "The sound of AUM",
    aumSub: "AUM = OM",
    aumNote: "Each syllable maps to a state of consciousness. The silence after — Turiya — is pure awareness.",
    statesLabel: "The four states — Avastha",
    connectionLabel: "The connection to Dreamfulness",
  },
  states: [
    { sanskrit: "Jagrat",   english: "Waking",             symbol: "A", description: "Outward awareness — the senses, logic, and the world of daily life." },
    { sanskrit: "Svapna",   english: "Dreaming",           symbol: "U", description: "Inward awareness — the mind creates its own reality. Where Dreamfulness works." },
    { sanskrit: "Sushupti", english: "Deep Sleep",         symbol: "M", description: "No awareness, no dreams — pure rest and deep physical restoration." },
    { sanskrit: "Turiya",   english: "Pure Consciousness", symbol: "∞", description: "The silent witness beneath all three states — the true Self, ever present." },
  ] as AumState[],
  aum: [
    { letter: "A", meaning: "Waking" },
    { letter: "U", meaning: "Dreaming" },
    { letter: "M", meaning: "Deep Sleep" },
    { letter: "∞", meaning: "Turiya" },
  ] as { letter: string; meaning: string }[],
  inspiration: [
    { t: "Long before neuroscience had instruments to measure the mind, the " },
    { t: "Mandukya Upanishad", b: true },
    { t: " — one of the shortest yet most profound Vedic texts — described the complete architecture of human consciousness. Dreamfulness draws directly from this framework." },
  ] as Seg[],
  connection: [
    { t: "The " },
    { t: "Svapna", b: true },
    { t: " state — the dream state — is where the subconscious expresses itself freely. Dreamfulness pairs this ancient insight with neuroscience to make dreaming a place of " },
    { t: "conscious, directed wellness", b: true, hi: true },
    { t: "." },
  ] as Seg[],
  canvas: {
    /** Orbit labels for A, U, M, ∞ (drawn uppercase). */
    aumLabels: ["Waking", "Dreaming", "Deep Sleep", "Turiya"],
    caption: "Mandukya Upanishad",
  },
};

export type Slide2Content = typeof en;

const de: Slide2Content = {
  meta: {
    breadcrumbPre: "Alte Wurzeln",
    breadcrumbAccent: "Mandukya Upanishad",
    headerPlain: "Alte",
    headerAccent: "Weisheit",
    titlePlain: "Mandukya",
    titleAccent: "Upanishad",
    subtitle: "Der uralte Text, der schon vor 3.000 Jahren das Bewusstsein kartierte",
    inspirationLabel: "Die Inspiration",
    aumLabel: "Der Klang von AUM",
    aumSub: "AUM = OM",
    aumNote: "Jede Silbe steht für einen Bewusstseinszustand. Die Stille danach – Turiya – ist reines Gewahrsein.",
    statesLabel: "Die vier Zustände – Avastha",
    connectionLabel: "Die Verbindung zu Dreamfulness",
  },
  states: [
    { sanskrit: "Jagrat",   english: "Wachen",             symbol: "A", description: "Nach außen gerichtetes Gewahrsein – die Sinne, die Logik und die Welt des Alltags." },
    { sanskrit: "Svapna",   english: "Träumen",            symbol: "U", description: "Nach innen gerichtetes Gewahrsein – der Geist erschafft seine eigene Realität. Hier wirkt Dreamfulness." },
    { sanskrit: "Sushupti", english: "Tiefschlaf",         symbol: "M", description: "Kein Gewahrsein, keine Träume – reine Ruhe und tiefe körperliche Erholung." },
    { sanskrit: "Turiya",   english: "Reines Bewusstsein", symbol: "∞", description: "Der stille Zeuge hinter allen drei Zuständen – das wahre Selbst, immer gegenwärtig." },
  ],
  aum: [
    { letter: "A", meaning: "Wachen" },
    { letter: "U", meaning: "Träumen" },
    { letter: "M", meaning: "Tiefschlaf" },
    { letter: "∞", meaning: "Turiya" },
  ],
  inspiration: [
    { t: "Lange bevor die Neurowissenschaft Instrumente hatte, um den Geist zu vermessen, beschrieb die " },
    { t: "Mandukya Upanishad", b: true },
    { t: " – einer der kürzesten und zugleich tiefgründigsten vedischen Texte – die gesamte Architektur des menschlichen Bewusstseins. Dreamfulness schöpft direkt aus diesem Modell." },
  ],
  connection: [
    { t: "Der " },
    { t: "Svapna", b: true },
    { t: "-Zustand – der Traumzustand – ist der Ort, an dem sich das Unterbewusstsein frei ausdrückt. Dreamfulness verbindet diese alte Einsicht mit Neurowissenschaft und macht das Träumen zu einem Ort " },
    { t: "bewussten, gezielten Wohlbefindens", b: true, hi: true },
    { t: "." },
  ],
  canvas: {
    aumLabels: ["Wachen", "Träumen", "Tiefschlaf", "Turiya"],
    caption: "Mandukya Upanishad",
  },
};

export const slide2Content: ByLang<Slide2Content> = { en, de };
export const useSlide2 = () => useL(slide2Content);
